import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { sanitizeActivityText } from "@/lib/sanitizeActivityText";

export interface UserNotification {
  id: string;
  user_id: string;
 type: 'email_reply' | 'contract_signed' | 'contract_viewed' | 'contract_comment' | 'contract_payment' | 'contract_renewal' | 'contract_reminder' | 'lead_update' | 'system' | 'task_assigned' | 'task_updated' | 'task_comment' | 'task_mention' | 'usage_warning' | 'lead_hard_bounced' | 'lead_soft_bounced_final' | 'email_opened' | 'email_clicked' | 'email_scheduled' | 'email_sent';
  title: string;
  message: string;
  link: string | null;
  lead_id: string | null;
  is_read: boolean;
  is_handled: boolean;
  created_at: string;
  lead_name?: string | null;
  lead_email?: string | null;
  lead_company?: string | null;
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      
      const typedData = (data || []).map(n => ({
        ...n,
        message: sanitizeActivityText(n.message) || 'Open the contact profile for details.',
        is_handled: n.is_handled ?? false,
      })) as unknown as UserNotification[];

      // Enrich with lead name / email / company so notifications, cards and
      // toasts can always show who the notification is about — and clicking
      // resolves to the correct lead profile.
      const leadIds = Array.from(
        new Set(typedData.map((n) => n.lead_id).filter(Boolean) as string[]),
      );
      if (leadIds.length > 0) {
        const { data: leads } = await supabase
          .from('leads')
          .select('id, name, email, company')
          .in('id', leadIds);
        const byId = new Map((leads || []).map((l: any) => [l.id, l]));
        for (const n of typedData) {
          if (n.lead_id && byId.has(n.lead_id)) {
            const l = byId.get(n.lead_id)!;
            n.lead_name = l.name || null;
            n.lead_email = l.email || null;
            n.lead_company = l.company || null;
          }
        }
      }

      setNotifications(typedData);
      setUnreadCount(typedData.filter(n => !n.is_read).length);
    } catch (error: any) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();

    // Subscribe to realtime notifications
    const channel = supabase
      .channel('user_notifications_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_notifications',
        },
        async (payload) => {
          const { data: { user } } = await supabase.auth.getUser();
          if (payload.new && (payload.new as any).user_id === user?.id) {
            const newNotification = {
              ...(payload.new as Record<string, unknown>),
              message: sanitizeActivityText(String((payload.new as Record<string, unknown>).message || '')) || 'Open the contact profile for details.',
              is_handled: (payload.new as Record<string, unknown>).is_handled ?? false,
            } as unknown as UserNotification;
            // Backfill lead info for the new row so it shows the lead name
            // immediately in the toast and popover.
            if (newNotification.lead_id) {
              const { data: l } = await supabase
                .from('leads')
                .select('id, name, email, company')
                .eq('id', newNotification.lead_id)
                .maybeSingle();
              if (l) {
                newNotification.lead_name = (l as any).name || null;
                newNotification.lead_email = (l as any).email || null;
                newNotification.lead_company = (l as any).company || null;
              }
            }
            setNotifications(prev => [newNotification, ...prev]);
            setUnreadCount(prev => prev + 1);

            const who = newNotification.lead_name || newNotification.lead_email;
            toast.info(who ? `${who} — ${newNotification.title}` : newNotification.title, {
              description: newNotification.message,
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_notifications',
        },
        async (payload) => {
          const { data: { user } } = await supabase.auth.getUser();
          if (payload.new && (payload.new as any).user_id === user?.id) {
            const updated = {
              ...(payload.new as Record<string, unknown>),
              is_handled: (payload.new as Record<string, unknown>).is_handled ?? false,
            } as unknown as UserNotification;
            setNotifications(prev =>
              prev.map(n => n.id === updated.id ? updated : n)
            );
            // Recalculate unread count
            setNotifications(prev => {
              setUnreadCount(prev.filter(n => !n.is_read).length);
              return prev;
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchNotifications]);

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('user_notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error: any) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('user_notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) throw error;

      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error: any) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      const notification = notifications.find(n => n.id === notificationId);
      
      const { error } = await supabase
        .from('user_notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error: any) {
      console.error("Error deleting notification:", error);
    }
  };

  // Filter helpers
  const getUnhandledEmailReplies = () => {
    return notifications.filter(n => n.type === 'email_reply' && !n.is_handled);
  };

  const getUnreadEmailReplies = () => {
    return notifications.filter(n => n.type === 'email_reply' && !n.is_read);
  };

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refetch: fetchNotifications,
    getUnhandledEmailReplies,
    getUnreadEmailReplies,
  };
}
